/**
 * Advanced Filter Utilities
 * Provides filter definitions, operators, and application logic for advanced filtering
 */

import { DateTime } from 'luxon';

// Operator definitions for different field types
export const OPERATORS = {
    text: [
        { value: 'equals', label: 'Equals' },
        { value: 'contains', label: 'Contains' },
        { value: 'not_contains', label: 'Does not contain' },
        { value: 'starts_with', label: 'Starts with' },
        { value: 'ends_with', label: 'Ends with' },
        { value: 'is_empty', label: 'Is empty' },
        { value: 'is_not_empty', label: 'Is not empty' },
    ],
    number: [
        { value: 'equals', label: 'Equals' },
        { value: 'greater_than', label: 'Greater than' },
        { value: 'less_than', label: 'Less than' },
        { value: 'between', label: 'Between' },
        { value: 'is_empty', label: 'Is empty' },
        { value: 'is_not_empty', label: 'Is not empty' },
    ],
    date: [
        { value: 'equals', label: 'On' },
        { value: 'before', label: 'Before' },
        { value: 'after', label: 'After' },
        { value: 'between', label: 'Between' },
        { value: 'past_n_days', label: 'Trailing Date' },
        { value: 'is_empty', label: 'Is empty' },
        { value: 'is_not_empty', label: 'Is not empty' },
    ],
    select: [
        { value: 'equals', label: 'Is' },
        { value: 'is_empty', label: 'Is empty' },
        { value: 'is_not_empty', label: 'Is not empty' },
    ],
    multiselect: [
        { value: 'contains_any', label: 'Contains any of' },
        { value: 'contains_all', label: 'Contains all of' },
        { value: 'not_contains_any', label: 'Does not contain any of' },
        { value: 'is_empty', label: 'Is empty' },
        { value: 'is_not_empty', label: 'Is not empty' },
    ],
    boolean: [
        { value: 'equals', label: 'Is' },
        { value: 'is_empty', label: 'Is not set' },
    ],
    sheetselector: [
        { value: 'equals', label: 'Is' },
        { value: 'is_empty', label: 'Is empty' },
        { value: 'is_not_empty', label: 'Is not empty' },
    ],
};

// Field type mapping for custom fields
export const CUSTOM_FIELD_TYPE_MAP = {
    'Text': 'text',
    'Long Text': 'text',
    'Number': 'number',
    'Date': 'date',
    'Datetime': 'date',
    'Checkbox': 'boolean',
    'Dropdown': 'multiselect',
    'Multiselect': 'multiselect'
};

// Predefined people filter fields
export const PEOPLE_FILTER_FIELDS = [
    {
        id: 'age',
        name: 'Age',
        type: 'number',
        field: 'dateOfBirth',
        category: 'basic',
        description: 'Age calculated from birth date',
    },
    {
        id: 'timesAttended',
        name: 'Times Attended',
        type: 'number',
        field: 'timesAttended',
        category: 'basic',
        description: 'Total number of events attended',
    },
    {
        id: 'gender',
        name: 'Gender',
        type: 'select',
        field: 'gender',
        category: 'basic',
        options: ['Male', 'Female'],
        allowEmpty: true,
        description: 'It\'s not complicated',
    },
    {
        id: 'roles',
        name: 'Roles',
        type: 'multiselect',
        field: 'Roles',
        category: 'basic',
        dynamicOptions: 'roles',
        allowEmpty: true,
        emptyLabel: 'Has No Roles',
        description: 'Roles assigned to the person',
    },
    {
        id: 'smallGroupTypes',
        name: 'Small Group Members',
        type: 'multiselect',
        field: 'SmallGroupTypes',
        category: 'basic',
        dynamicOptions: 'smallGroupTypes',
        allowEmpty: true,
        emptyLabel: 'Has No Small Groups',
        description: 'Small group types the person is a member of',
    },
    {
        id: 'invitedBy',
        name: 'Invited By',
        type: 'sheetselector',
        field: 'invitedByID',
        category: 'basic',
        sheetSelectorType: 'people',
        allowEmpty: true,
        emptyLabel: 'No Invited By',
        description: 'Person who invited this person',
    },
    {
        id: 'mostRecentVisit',
        name: 'Last Visit',
        type: 'date',
        field: 'mostRecentVisit',
        category: 'basic',
        description: 'Most recent date of check in',
    },
    {
        id: 'firstVisit',
        name: 'First Visit',
        type: 'date',
        field: 'firstVisit',
        category: 'basic',
        description: 'Earliest date of check in',
    },
    {
        id: 'updatedAt',
        name: 'Last Updated',
        type: 'date',
        field: 'updatedAt',
        category: 'basic',
        description: 'When the last change was made',
    },
    {
        id: 'createdAt',
        name: 'Date Added',
        type: 'date',
        field: 'createdAt',
        category: 'basic',
        description: 'When the person was first added',
    },
    {
        id: 'hasAccount',
        name: 'Has Account',
        type: 'boolean',
        field: 'RegisteredUsersOrganizations',
        category: 'basic',
        description: 'Whether the person has a Reach account connected',
    },
];

/**
 * Creates a new filter condition with default values
 * @param {object} field - The field definition
 * @returns {object} New filter condition
 */
export const createFilterCondition = (field) => {
    const fieldType = field.customFieldType 
        ? CUSTOM_FIELD_TYPE_MAP[field.customFieldType] || 'text'
        : field.type;
    
    const operators = OPERATORS[fieldType] || OPERATORS.text;
    
    return {
        id: `filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        fieldId: field.id,
        fieldName: field.name,
        fieldType: fieldType,
        customFieldType: field.customFieldType,
        customFieldId: field.customFieldId,
        operator: operators[0].value,
        value: fieldType === 'multiselect' ? [] : '',
        value2: '', // For 'between' operators
        options: field.options || [],
        dynamicOptions: field.dynamicOptions,
        allowEmpty: field.allowEmpty,
        emptyLabel: field.emptyLabel,
    };
};

/**
 * Calculates age from date of birth
 * @param {string|Date} dateOfBirth 
 * @returns {number|null}
 */
export const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;

    const today = DateTime.now();
    const birthDate = DateTime.fromISO(dateOfBirth);
    let age = today.year - birthDate.year;
    const m = today.month - birthDate.month;

    if (m < 0 || (m === 0 && today.day < birthDate.day)) {
        age--;
    }

    return age;
};

/**
 * Evaluates a single filter condition against a value
 * @param {*} fieldValue - The value to test
 * @param {object} condition - The filter condition
 * @returns {boolean}
 */
const evaluateCondition = (fieldValue, condition) => {
    const { operator, value, value2, fieldType } = condition;
    
    // Handle empty checks first
    if (operator === 'is_empty') {
        if (Array.isArray(fieldValue)) return fieldValue.length === 0;
        return fieldValue === null || fieldValue === undefined || fieldValue === '';
    }
    
    if (operator === 'is_not_empty') {
        if (Array.isArray(fieldValue)) return fieldValue.length > 0;
        return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
    }
    
    // If field value is empty and we're not checking for emptiness, condition fails
    if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
        return false;
    }
    
    switch (fieldType) {
        case 'text':
            return evaluateTextCondition(fieldValue, operator, value);
        case 'number':
            return evaluateNumberCondition(fieldValue, operator, value, value2);
        case 'date':
            return evaluateDateCondition(fieldValue, operator, value, value2);
        case 'select':
            return evaluateSelectCondition(fieldValue, operator, value);
        case 'multiselect':
            return evaluateMultiselectCondition(fieldValue, operator, value);
        case 'boolean':
            return evaluateBooleanCondition(fieldValue, operator, value);
        default:
            return true;
    }
};

const evaluateTextCondition = (fieldValue, operator, value) => {
    const normalizedField = String(fieldValue).toLowerCase();
    const normalizedValue = String(value).toLowerCase();
    
    switch (operator) {
        case 'equals':
            return normalizedField === normalizedValue;
        case 'not_equals':
            return normalizedField !== normalizedValue;
        case 'contains':
            return normalizedField.includes(normalizedValue);
        case 'not_contains':
            return !normalizedField.includes(normalizedValue);
        case 'starts_with':
            return normalizedField.startsWith(normalizedValue);
        case 'ends_with':
            return normalizedField.endsWith(normalizedValue);
        default:
            return true;
    }
};

const evaluateNumberCondition = (fieldValue, operator, value, value2) => {
    const numField = Number(fieldValue);
    const numValue = Number(value);
    const numValue2 = Number(value2);
    
    if (isNaN(numField)) return false;
    
    switch (operator) {
        case 'equals':
            return numField === numValue;
        case 'not_equals':
            return numField !== numValue;
        case 'greater_than':
            return numField > numValue;
        case 'less_than':
            return numField < numValue;
        case 'between':
            return numField >= numValue && numField <= numValue2;
        default:
            return true;
    }
};

const evaluateDateCondition = (fieldValue, operator, value, value2) => {
    const dateField = new Date(fieldValue);
    
    if (isNaN(dateField.getTime())) return false;
    
    // Normalize dates to start of day for comparison
    const normalizeDate = (d) => {
        const normalized = new Date(d);
        normalized.setHours(0, 0, 0, 0);
        return normalized;
    };
    
    const normalizedField = normalizeDate(dateField);
    
    // Handle past_n_days operator - value is the number of days
    if (operator === 'past_n_days') {
        const days = Number(value);
        if (isNaN(days) || days < 0) return false;
        
        const today = normalizeDate(new Date());
        const pastDate = new Date(today);
        pastDate.setDate(pastDate.getDate() - days);
        
        return normalizedField >= pastDate && normalizedField <= today;
    }
    
    const dateValue = new Date(value);
    const normalizedValue = normalizeDate(dateValue);
    
    if (operator === 'equals') {
        return normalizedField.getTime() === normalizedValue.getTime();
    }
    if (operator === 'not_equals') {
        return normalizedField.getTime() !== normalizedValue.getTime();
    }
    if (operator === 'before') {
        return normalizedField < normalizedValue;
    }
    if (operator === 'after') {
        return normalizedField > normalizedValue;
    }
    if (operator === 'between') {
        const normalizedValue2 = normalizeDate(new Date(value2));
        return normalizedField >= normalizedValue && normalizedField <= normalizedValue2;
    }
    
    return true;
};

const evaluateSelectCondition = (fieldValue, operator, value) => {
    const normalizedField = String(fieldValue).toLowerCase();
    const normalizedValue = String(value).toLowerCase();
    
    switch (operator) {
        case 'equals':
            return normalizedField === normalizedValue;
        case 'not_equals':
            return normalizedField !== normalizedValue;
        default:
            return true;
    }
};

const evaluateMultiselectCondition = (fieldValue, operator, value) => {
    const fieldArray = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
    const valueArray = Array.isArray(value) ? value : [value];
    
    if (valueArray.length === 0) return true;
    
    // Normalize for comparison - handle both string arrays and object arrays with id
    const normalizeItem = (item) => {
        if (typeof item === 'object' && item !== null) {
            return item.id || item.value || String(item);
        }
        return String(item).toLowerCase();
    };
    
    const normalizedField = fieldArray.map(normalizeItem);
    const normalizedValue = valueArray.map(normalizeItem);
    
    switch (operator) {
        case 'contains_any':
            return normalizedValue.some(v => normalizedField.includes(v));
        case 'contains_all':
            return normalizedValue.every(v => normalizedField.includes(v));
        case 'not_contains_any':
            return !normalizedValue.some(v => normalizedField.includes(v));
        default:
            return true;
    }
};

const evaluateBooleanCondition = (fieldValue, operator, value) => {
    const boolField = fieldValue === true || fieldValue === 'true' || fieldValue === 1;
    const boolValue = value === true || value === 'true' || value === 'On' || value === 1;
    
    switch (operator) {
        case 'equals':
            return boolField === boolValue;
        default:
            return true;
    }
};

/**
 * Gets the field value from a person object based on condition
 * @param {object} person - The person object
 * @param {object} condition - The filter condition
 * @returns {*} The field value
 */
const getFieldValue = (person, condition) => {
    const { fieldId, customFieldId } = condition;
    
    // Handle custom fields
    if (customFieldId) {
        return person.customFieldValues?.[customFieldId];
    }
    
    // Handle predefined fields
    switch (fieldId) {
        case 'age':
            return calculateAge(person.dateOfBirth);
        case 'gender':
            return person.gender;
        case 'roles':
            return person.Roles?.map(r => r.id) || [];
        case 'updatedAt':
            return person.updatedAt;
        case 'createdAt':
            return person.createdAt;
        case 'hasAccount':
            return (person.RegisteredUsersOrganizations?.length > 0) || false;
        default:
            return person[fieldId];
    }
};

/**
 * Applies advanced filters to a list of people
 * @param {Array} people - Array of person objects
 * @param {Array} conditions - Array of filter conditions
 * @param {string} logic - 'AND' or 'OR'
 * @returns {Array} Filtered array of people
 */
export const applyAdvancedFilters = (people, conditions, logic = 'AND') => {
    if (!conditions || conditions.length === 0) {
        return people;
    }
    
    return people.filter(person => {
        const results = conditions.map(condition => {
            const fieldValue = getFieldValue(person, condition);
            return evaluateCondition(fieldValue, condition);
        });
        
        return logic === 'AND' 
            ? results.every(r => r === true)
            : results.some(r => r === true);
    });
};

/**
 * Converts filter conditions to a serializable format for storage
 * @param {Array} conditions - Array of filter conditions
 * @param {string} logic - Filter logic
 * @returns {object} Serializable filter object
 */
export const serializeFilters = (conditions, logic) => {
    return {
        conditions: conditions.map(c => {
            // Handle sheetselector type - extract just the ID from the value object
            let serializedValue = c.value;
            if (c.fieldType === 'sheetselector' && c.value) {
                if (typeof c.value === 'object' && c.value !== null) {
                    // If value is an object with id, extract just the id
                    serializedValue = c.value.id || c.value;
                }
            }
            
            return {
                fieldId: c.fieldId,
                fieldName: c.fieldName,
                fieldType: c.fieldType,
                customFieldType: c.customFieldType,
                customFieldId: c.customFieldId,
                operator: c.operator,
                value: serializedValue,
                value2: c.value2,
                options: c.options,
                dynamicOptions: c.dynamicOptions,
                allowEmpty: c.allowEmpty,
                emptyLabel: c.emptyLabel,
            };
        }),
        logic,
    };
};

/**
 * Deserializes stored filters back to condition objects
 * @param {object} serialized - Serialized filter object
 * @returns {object} Object with conditions array and logic
 */
export const deserializeFilters = (serialized) => {
    if (!serialized || !serialized.conditions) {
        return { conditions: [], logic: 'AND' };
    }
    
    return {
        conditions: serialized.conditions.map(c => ({
            ...c,
            id: `filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        })),
        logic: serialized.logic || 'AND',
    };
};

/**
 * Gets available fields for filtering (predefined + custom fields)
 * @param {Array} customFields - Array of custom field definitions
 * @param {string[]|null} [allowedFieldIds] - When provided, only fields with a
 *   matching id survive (plan gating: Free orgs pass the backend allowlist)
 * @returns {Array} Combined array of filter field definitions
 */
export const getAvailableFilterFields = (customFields = [], allowedFieldIds = null) => {
    const customFilterFields = customFields.map(cf => ({
        id: `custom_${cf.id}`,
        customFieldId: cf.id,
        name: cf.name,
        type: CUSTOM_FIELD_TYPE_MAP[cf.type] || 'text',
        customFieldType: cf.type,
        category: 'custom',
        options: cf.options || [],
        sectionID: cf.Section?.id || null,
        sectionName: cf.Section?.name || null,
        sectionPosition: cf.Section?.position ?? 1000,
        fieldPosition: cf.position ?? 1000,
    }));

    const predefined = allowedFieldIds
        ? PEOPLE_FILTER_FIELDS.filter(f => allowedFieldIds.includes(f.id))
        : PEOPLE_FILTER_FIELDS;
    const customs = allowedFieldIds
        ? customFilterFields.filter(f => allowedFieldIds.includes(f.id))
        : customFilterFields;

    return [
        ...predefined,
        ...customs,
    ];
};

/**
 * Groups filter fields by category
 * @param {Array} fields - Array of filter field definitions
 * @returns {object} Object with fields grouped by category
 */
export const groupFieldsByCategory = (fields) => {
    return fields.reduce((acc, field) => {
        const category = field.category || 'other';
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(field);
        return acc;
    }, {});
};

/**
 * Groups filter fields into sections ordered by section position,
 * with fields within each section ordered by their field position.
 * Basic fields go into a "Basic Fields" section first.
 * Custom fields are grouped by their PersonCustomFieldSection.
 * Custom fields without a section go into "Other" at the end.
 * @param {Array} fields - Array of filter field definitions
 * @returns {Array} Array of { sectionName, sectionPosition, fields } objects, sorted by sectionPosition
 */
export const groupFieldsBySections = (fields) => {
    const sectionMap = {};

    fields.forEach(field => {
        if (field.category === 'basic') {
            if (!sectionMap['__basic__']) {
                sectionMap['__basic__'] = { sectionName: 'Basic Fields', sectionPosition: -1, fields: [] };
            }
            sectionMap['__basic__'].fields.push(field);
        } else if (field.sectionID) {
            const key = field.sectionID;
            if (!sectionMap[key]) {
                sectionMap[key] = { sectionName: field.sectionName, sectionPosition: field.sectionPosition, fields: [] };
            }
            sectionMap[key].fields.push(field);
        } else {
            if (!sectionMap['__other__']) {
                sectionMap['__other__'] = { sectionName: 'Other', sectionPosition: 9999, fields: [] };
            }
            sectionMap['__other__'].fields.push(field);
        }
    });

    // Sort sections by sectionPosition, then sort fields within each section by fieldPosition
    return Object.values(sectionMap)
        .sort((a, b) => a.sectionPosition - b.sectionPosition)
        .map(section => ({
            ...section,
            fields: section.fields.sort((a, b) => {
                const posA = a.fieldPosition ?? 1000;
                const posB = b.fieldPosition ?? 1000;
                if (posA !== posB) return posA - posB;
                return (a.name || '').localeCompare(b.name || '');
            }),
        }));
};
